import { Button, Link } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import UserAddModal from "features/settings/users/AddUserModal";
import { UserRoleDefinitionsModal } from "features/settings/users/modal/UserRoleDefinitionsModal";
import {
  getUsersAdminTableDataRetrieveQueryKey,
  useUsersAdminTableDataRetrieveSuspense,
  useUsersAdminTableDataUpdate,
} from "gen/endpoints/users/users";
import { UserRoleUpdateAPIDataRequest } from "gen/model/userRoleUpdateAPIDataRequest";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const UsersPage: React.FC = () => {
  const { t } = useTranslation(["common", "settings", "internal"]);
  const form = useForm<
    UserRoleUpdateAPIDataRequest & { editModeEnabled: boolean }
  >({});
  const editModeEnabled = form.watch("editModeEnabled", false);

  const { data: tableData, refetch: refetchTableData } =
    useUsersAdminTableDataRetrieveSuspense({ edits_enabled: editModeEnabled });
  const { mutateAsync: updateUsers } = useUsersAdminTableDataUpdate();

  const {
    value: isUserAddModalOpen,
    setTrue: openUserAddModal,
    setFalse: closeUserAddModal,
  } = useBoolean();

  const {
    value: isRoleDefinitionsModalOpen,
    setTrue: openRoleDefinitionsModal,
    setFalse: closeRoleDefinitionsModal,
  } = useBoolean();

  const queryClient = useQueryClient();

  const onSubmit = async (data: UserRoleUpdateAPIDataRequest) => {
    await updateUsers({
      data: { user_id_to_edit_data: data.user_id_to_edit_data },
    });
    form.setValue("editModeEnabled", false);
  };

  const onSuccess = async () => {
    await Promise.allSettled([
      invalidateQueryKeys(
        queryClient,
        getUsersAdminTableDataRetrieveQueryKey({ edits_enabled: true }),
      ),
      invalidateQueryKeys(
        queryClient,
        getUsersAdminTableDataRetrieveQueryKey({ edits_enabled: false }),
      ),
    ]);
  };

  return (
    <PageTabBody>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={onSuccess}
        successMessage={t("internal:modal.updateRoles.success")}
      >
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:users")}</TableBoxTitle>
            <DataTableBoxActions>
              <Link mr={2} onClick={openRoleDefinitionsModal}>
                {t("settings:roleDescriptions")}
              </Link>
              {!editModeEnabled && (
                <Button
                  size="xs"
                  onClick={async () => {
                    form.setValue("editModeEnabled", true);
                    await refetchTableData();
                  }}
                >
                  {t("internal:modal.updateRoles.title")}
                </Button>
              )}
              <Button size="xs" onClick={openUserAddModal}>
                {t("settings:user.add.title")}
              </Button>
            </DataTableBoxActions>
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={tableData} />
          </TableBoxBody>
        </TableBox>
        {editModeEnabled && (
          <FormFooter>
            <FormFooterActions>
              <ButtonOrder
                dismiss={
                  <FormCancelButton
                    onClick={() => form.setValue("editModeEnabled", false)}
                  />
                }
                confirm={
                  <FormSubmitButton>{t("common:saveChanges")}</FormSubmitButton>
                }
              />
            </FormFooterActions>
          </FormFooter>
        )}
      </Form>
      <QueryBoundary>
        {isUserAddModalOpen && (
          <UserAddModal
            onSuccess={async () => {
              await onSuccess();
              closeUserAddModal();
            }}
            onCloseComplete={closeUserAddModal}
          />
        )}
        {isRoleDefinitionsModalOpen && (
          <UserRoleDefinitionsModal
            onCloseComplete={closeRoleDefinitionsModal}
          />
        )}
      </QueryBoundary>
    </PageTabBody>
  );
};
